import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import { DASHBOARD_ACCENT, resolveDashboardThemeMode } from './dashboardSurfaceSx';
import {
  getDashboardWorkspaceInnerSectionSx,
  getDashboardWorkspaceSectionHeaderSx,
  getDashboardWorkspaceSectionTitleSx,
  getDashboardWorkspaceSummaryChipSx,
} from './dashboardWorkspaceLayout';

export const GRID_STATUS_COLORS = {
  checked_in: '#2b8a3e',
  partial_checkin: '#2b8a3e',
  partial_checkout: '#0b7285',
  confirmed: '#e8590c',
  booked: '#e67700',
  pending: '#d9480f',
  reserved: '#5f3dc4',
  checked_out: '#868e96',
  cancelled: '#a61e4d',
  canceled: '#a61e4d',
  no_show: '#862e9c',
  blocked: '#c92a2a',
  web_checkin: '#0ca678',
  other: '#868e96',
};

export function getGridStatusColor(status) {
  const key = String(status || 'other').toLowerCase().replace(/\s+/g, '_');
  return GRID_STATUS_COLORS[key] || GRID_STATUS_COLORS.other;
}

export function getGridSectionPaperSx(themeMode) {
  return {
    ...getDashboardWorkspaceInnerSectionSx(themeMode),
    mb: 0,
    overflow: 'visible',
    borderRadius: 0,
  };
}

export function getGridSectionHeaderSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    ...getDashboardWorkspaceSectionHeaderSx(themeMode, DASHBOARD_ACCENT),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: `linear-gradient(90deg, transparent 0%, ${alpha(DASHBOARD_ACCENT, 0.18)} 20%, ${alpha(DASHBOARD_ACCENT, 0.28)} 50%, ${alpha(DASHBOARD_ACCENT, 0.18)} 80%, transparent 100%)`,
    },
  };
}

export function getGridSectionTitleSx(themeMode) {
  return getDashboardWorkspaceSectionTitleSx(themeMode);
}

export function getGridSectionCountChipSx(themeMode) {
  return getDashboardWorkspaceSummaryChipSx(themeMode, DASHBOARD_ACCENT);
}

export function getGridSummaryChipSx(themeMode, status) {
  return getDashboardWorkspaceSummaryChipSx(themeMode, getGridStatusColor(status));
}

export function getGridSummaryLabelSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    color: isDark ? alpha('#cbd5e1', 0.62) : '#64748b',
    fontWeight: 800,
    textTransform: 'uppercase',
    fontSize: '0.58rem',
    letterSpacing: '0.08em',
  };
}

export function getGridLineColor(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return isDark ? alpha('#ffffff', 0.18) : alpha('#0f172a', 0.2);
}

export function getGridColumnLineColor(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.08);
}

/** Room label + equal-width day columns that fill the available width (no horizontal scroll). */
export function getWorkspaceGridTemplateColumns(roomLabelWidth, dayCount) {
  const safeDayCount = Math.max(1, Number(dayCount) || 1);
  return `${roomLabelWidth}px repeat(${safeDayCount}, minmax(0, 1fr))`;
}

export function getGridDateHeaderRowSx(themeMode, roomLabelWidth, dayCount) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  const useFluidGrid = roomLabelWidth != null && dayCount != null;
  return {
    display: useFluidGrid ? 'grid' : 'flex',
    ...(useFluidGrid
      ? { gridTemplateColumns: getWorkspaceGridTemplateColumns(roomLabelWidth, dayCount) }
      : {}),
    width: '100%',
    minWidth: 0,
    borderBottom: `1px solid ${getGridLineColor(themeMode)}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : '#f1f5f9',
    position: 'relative',
    overflow: 'visible',
  };
}

export function getGridRoomsColumnHeaderSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    p: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 0.35,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
    borderRight: `1px solid ${getGridColumnLineColor(themeMode)}`,
  };
}

export function getGridRoomsColumnLabelSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    fontWeight: 800,
    fontSize: '0.58rem',
    color: isDark ? alpha('#e2e8f0', 0.72) : '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  };
}

export function formatRoomChipLabel(room) {
  const number = String(room?.room_number || room?.id || '').trim();
  const code = String(room?.room_code || '').trim();
  if (code && number && code !== number) {
    return `${code}-${number}`;
  }
  return number || `Room ${room?.id}`;
}

export function getGridRoomLabelColumnSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'relative',
    pl: 0,
    pr: 0.5,
    py: 0,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : '#fff',
    borderRight: `1px solid ${getGridColumnLineColor(themeMode)}`,
    borderBottom: `1px solid ${getGridLineColor(themeMode)}`,
  };
}

export function getGridRoomNumberBadgeSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    maxWidth: '100%',
    height: 'auto',
    px: 0.35,
    py: 0.15,
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.68rem',
    fontWeight: 800,
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: DASHBOARD_ACCENT,
    bgcolor: alpha(DASHBOARD_ACCENT, isDark ? 0.14 : 0.1),
    border: `1px solid ${alpha(DASHBOARD_ACCENT, isDark ? 0.32 : 0.24)}`,
    boxShadow: isDark ? `0 0 12px ${alpha(DASHBOARD_ACCENT, 0.12)}` : 'none',
  };
}

export function getGridRoomCodeSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    fontSize: '0.58rem',
    fontWeight: 700,
    color: isDark ? alpha('#cbd5e1', 0.55) : '#64748b',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  };
}

export function getGridDayCellSx(
  themeMode,
  { isWeekend = false, canQuick = false, isToday = false, minWidth = 96, fluid = false } = {},
) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  const baseBg = isDark ? THEME_COLORS.BACKGROUND_DARK : '#f8fafc';
  const weekendBg = isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#f1f5f9';
  const todayBg = isDark ? '#1c2230' : '#eef2f7';
  const gridLine = getGridLineColor(themeMode);
  const hoverBg = isDark ? '#222836' : '#e2e8f0';
  const hoverBorder = alpha(DASHBOARD_ACCENT, isDark ? 0.24 : 0.35);

  return {
    ...(fluid
      ? { minWidth: 0, width: '100%', maxWidth: 'none' }
      : {
          flex: `0 0 ${minWidth}px`,
          width: `${minWidth}px`,
          minWidth: `${minWidth}px`,
          maxWidth: `${minWidth}px`,
        }),
    position: 'relative',
    borderRight: `1px solid ${getGridColumnLineColor(themeMode)}`,
    borderBottom: `1px solid ${gridLine}`,
    background: isToday ? todayBg : isWeekend ? weekendBg : baseBg,
    ...(isToday
      ? { boxShadow: `inset 2px 0 0 ${alpha(DASHBOARD_ACCENT, 0.22)}, inset -2px 0 0 ${alpha(DASHBOARD_ACCENT, 0.08)}` }
      : {}),
    cursor: canQuick ? 'pointer' : 'default',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    ...(canQuick
      ? {
          '&:hover': {
            backgroundColor: hoverBg,
            boxShadow: `inset 0 0 0 1px ${hoverBorder}`,
          },
        }
      : {}),
  };
}

export function getGridRowSx(themeMode, isLastRow, roomLabelWidth, dayCount) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  const useFluidGrid = roomLabelWidth != null && dayCount != null;
  return {
    display: useFluidGrid ? 'grid' : 'flex',
    ...(useFluidGrid
      ? { gridTemplateColumns: getWorkspaceGridTemplateColumns(roomLabelWidth, dayCount) }
      : {}),
    width: '100%',
    minWidth: 0,
    borderBottom: `1px solid ${getGridLineColor(themeMode)}`,
    position: 'relative',
    overflow: 'visible',
    '&:hover': {
      backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : alpha('#000', 0.015),
    },
  };
}

export function getGridDayRangeToggleSx(themeMode) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    py: 0.2,
    px: 0.2,
    borderRadius: '10px',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
    flexShrink: 0,
    '& .MuiToggleButtonGroup-grouped': {
      border: 0,
      borderRadius: '8px !important',
      mx: 0.1,
      minHeight: 24,
      minWidth: 32,
      px: 0.65,
      py: 0.35,
      fontSize: '0.58rem',
      fontWeight: 800,
      textTransform: 'none',
      letterSpacing: '0.02em',
      color: isDark ? alpha('#f8fafc', 0.45) : alpha('#0f172a', 0.45),
      '&.Mui-selected': {
        backgroundColor: `${alpha('#ffffff', isDark ? 0.08 : 0.1)} !important`,
        color: `${isDark ? '#e2e8f0' : '#0f172a'} !important`,
        boxShadow: `inset 0 0 0 1px ${alpha(DASHBOARD_ACCENT, isDark ? 0.2 : 0.28)}`,
      },
      '&.Mui-selected:hover': {
        backgroundColor: `${alpha('#ffffff', isDark ? 0.1 : 0.12)} !important`,
      },
    },
  };
}
